import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { verify } from "@/helpers/verifyAdmin";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";

import { CategoryResult } from "@/types/supabase";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory
} from "@/utils/supabase/categoryHelpers";
import CategoryRow from "@/components/admin/CategoryRow";
import CreateCategoryDialog from "@/components/admin/CreateCategoryDialog";
import DeleteCategoryDialog from "@/components/admin/DeleteCategoryDialog";
import AdminLayout from "@/components/admin/AdminLayout";
import {
  Plus
} from "lucide-react";

const CategoryManagementPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isCreating, setIsCreating] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryIcon, setNewCategoryIcon] = useState("");
  const [categoryToDelete, setCategoryToDelete] = useState<CategoryResult | null>(null);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editCategoryIcon, setEditCategoryIcon] = useState("");

  verify(user, navigate);

  const { data: categories, isLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });

  const createMutation = useMutation({
    mutationFn: (newCategory: { name: string; icon?: string }) => 
      createCategory(newCategory),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Category created successfully");
      setIsCreating(false);
      resetCreateForm();
    },
    onError: (error) => {
      toast.error(`Failed to create category: ${error}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; icon?: string } }) => 
      updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Category updated successfully");
      setIsEditing(null);
    },
    onError: (error) => {
      toast.error(`Failed to update category: ${error}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Category deleted successfully");
      setCategoryToDelete(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete category: ${error}`);
    },
  });

  const handleCreateCategory = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) {
      toast.error("Category name is required");
      return;
    }

    const newCategory: { name: string; icon?: string } = {
      name: newCategoryName.trim(),
    };
    
    if (newCategoryIcon.trim()) {
      newCategory.icon = newCategoryIcon.trim();
    }

    createMutation.mutate(newCategory);
  };

  const resetCreateForm = () => {
    setNewCategoryName("");
    setNewCategoryIcon("");
  };

  const handleEditClick = (category: CategoryResult) => {
    setIsEditing(category.id);
    setEditCategoryName(category.name);
    setEditCategoryIcon(category.icon || "");
  };

  if (isLoading) {
    return <AdminLayout title="Manage Categories" loading={true} />;
  }

  return (
    <AdminLayout title="Manage Categories">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            {/* Removed the Debug Icons button */}
          </div>
          <Button onClick={() => setIsCreating(true)} className="flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add Category
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/3">Name</TableHead>
                <TableHead className="w-1/2">Icon</TableHead>
                <TableHead className="w-[150px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories && categories.length > 0 ? (
                categories.map((category) => (
                  <CategoryRow
                    key={category.id}
                    category={category}
                    isEditing={isEditing === category.id}
                    editCategoryName={editCategoryName}
                    editCategoryIcon={editCategoryIcon}
                    updateMutation={updateMutation}
                    onEditClick={handleEditClick}
                    onDeleteClick={(category) => setCategoryToDelete(category)}
                    setIsEditing={setIsEditing}
                    setEditCategoryName={setEditCategoryName}
                    setEditCategoryIcon={setEditCategoryIcon}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                    No categories found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <CreateCategoryDialog
          isOpen={isCreating}
          onOpenChange={setIsCreating}
          newCategoryName={newCategoryName}
          newCategoryIcon={newCategoryIcon}
          setNewCategoryName={setNewCategoryName}
          setNewCategoryIcon={setNewCategoryIcon}
          onSubmit={handleCreateCategory}
          isPending={createMutation.isPending}
          resetForm={resetCreateForm}
        />

        <DeleteCategoryDialog
          category={categoryToDelete}
          onOpenChange={(open) => !open && setCategoryToDelete(null)}
          onConfirmDelete={() => categoryToDelete && deleteMutation.mutate(categoryToDelete.id)}
          isPending={deleteMutation.isPending}
        />
      </div>
    </AdminLayout>
  );
};

export default CategoryManagementPage;
